import React, { FC } from 'react';
import { Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import produce from 'immer';
import { get, set, remove } from 'lodash';

import Form from '~/components/WizardForm';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import { BankNameSelect, Checkbox, DocumentInput, Space, SpaceBetweenField } from '~/components';
import { useGetApplication } from '~/controllers/wizard';
import { useAmendment } from '~/controllers/amendment';
import LtrInput from '~/components/inputs/LtrInput';
import CustomFields from '~/components/CustomFields';
import { useRefScrollTo } from '~/hooks/useRefScrollTo';
import CustomInput from '~/components/inputs/CustomInput';
import { useHandbook } from '~/controllers/handbook';

const stepKey = 'bankAccount';
const n = (namePath: string | string[]) => [stepKey].concat([namePath].flat());
const BankAccount: FC = () => {
  const ref = useRefScrollTo();
  const { t } = useTranslation('fillApplication');
  const handbook = useHandbook();
  const options = handbook.data?.handbooks?.find((item) => item.key === 'banks');

  const applicationQuery = useGetApplication();
  const { isAmendment } = useAmendment();
  if (!applicationQuery.data) return null;
  const hasDevice =
    !!applicationQuery.data?.payload.services?.nGeniusPos?.enabled ||
    !!applicationQuery.data?.payload.services?.nGeniusGo?.enabled;
  const hasEcommerce = !!applicationQuery.data?.payload.services?.eCommerce?.enabled;
  const hasPayByQR = !!applicationQuery.data?.payload.services?.DIGITAL_BUSINESS_PAYMENT?.enabled;
  const legalName = applicationQuery.data?.payload.companyInformation?.companyLicense?.legalName;
  // const shouldRenderDevicesAccount = hasDevice || hasPayByQR;

  function parseValuesBankFn(values: any) {
    const bankName = values?.bankAccount?.bankName;
    const bankId = options?.values?.find((item) => bankName === item?.localizedValue);

    return {
      bankAccount: {
        ...values?.bankAccount,
        bankId: bankId?.apiValue,
      },
    };
  }

  return (
    <Container className="BankAccount" ref={ref}>
      <Form
        stepKey={stepKey}
        setInitialValues={(initialValues) => {
          return produce(initialValues, (draft: any) => {
            if (typeof get(draft, isSameAsDevicesAccountPath) !== 'boolean' || !hasDevice) {
              return;
              set(draft, isSameAsDevicesAccountPath, false);
            }
          });
        }}
        parseBeforeSubmit={(value: any) => parseValuesBankFn(value)}
      >
        <Typography.Title>{t('Bank Accounts')}</Typography.Title>
        <Typography.Title level={4}>{t('CORPORATE ACCOUNT DETAILS').toUpperCase()}</Typography.Title>
        {/* {shouldRenderDevicesAccount && ( */}
        {true && (
          <Section formItemLast>
            {hasDevice && <Typography.Title level={4}>{t('Account for devices').toUpperCase()}</Typography.Title>}
            {hasPayByQR && (
              <Typography.Title level={4}>
                {t('Bank account details (for credit/debit of sales proceeds)').toUpperCase()}
              </Typography.Title>
            )}
            <Typography.Paragraph>{t('Please upload scan of bank statement with IBAN number.')}</Typography.Paragraph>
            <Form.Item name={n(['files'])}>
              <DocumentInput name="photos" variant="default" />
            </Form.Item>
            <Typography.Paragraph>
              {t('Please share bank account details. The account must belong to specificed company')}
            </Typography.Paragraph>
            <InputsGroupLayout>
              <Form.Item label={t('Bank Name')} name={n(['bankName'])} disabled={isAmendment}>
                <BankNameSelect />
              </Form.Item>
              <Form.Item label={t('Branch Name')} name={n(['branchName'])} disabled={isAmendment}>
                <Input />
              </Form.Item>
              <Form.Item label="IBAN" name={n(['IBAN'])} disabled={isAmendment}>
                <LtrInput />
              </Form.Item>
            </InputsGroupLayout>
          </Section>
        )}
        {false && (
          <Section formItemLast>
            <Typography.Title level={4}>{t('Account for E-commerce').toUpperCase()}</Typography.Title>
            {hasDevice ? (
              <Form.Item name={isSameAsDevicesAccountPath} valuePropName="checked">
                <Checkbox>{t('Use the same account')}</Checkbox>
              </Form.Item>
            ) : (
              <Form.Item name={isSameAsDevicesAccountPath} noStyle />
            )}
            <Form.Visible name={isSameAsDevicesAccountPath} visibleIf={false}>
              <Typography.Paragraph>{t('Please upload scan of the bank statement')}</Typography.Paragraph>
              <Form.Item name={n(['ecommerceAccount', 'files'])}>
                <DocumentInput name="photos" variant="default" />
              </Form.Item>
              <InputsGroupLayout>
                <Form.Item label={t('Bank name')} name={n(['ecommerceAccount', 'bankName'])}>
                  <BankNameSelect />
                </Form.Item>
                <Form.Item label="IBAN" name={n(['ecommerceAccount', 'IBAN'])}>
                  <LtrInput />
                </Form.Item>
                <Form.Item label={t('Account title')} name={n(['ecommerceAccount', 'accountName'])}>
                  <CustomInput hintText={t('Account title has to be same as on bank statement.')} />
                </Form.Item>
                <Form.Item label={t('Account number')} name={n(['ecommerceAccount', 'accountNumber'])}>
                  <LtrInput />
                </Form.Item>
              </InputsGroupLayout>
            </Form.Visible>
          </Section>
        )}
        <CustomFields />
      </Form>
    </Container>
  );
};

const isSameAsDevicesAccountPath = n(['ecommerceAccount', 'isSameAsDevicesAccount']);

const Container = styled.div`
  //
`;

export default BankAccount;
